import {z} from 'zod';
import {openadrMessageIdSchema, requestIdSchema} from './id';
import {failureReasonSchema, messageStatusSchema} from './request';

/**
 * One row per resource group: one `FLEX` event on the wire.
 *
 * The `event_body` column is deliberately absent. The Data API caps a result at 1 MB, so
 * a sweep reading two hundred messages with their bodies fails on volume alone — leaving
 * the body out of the row type means a list or sweep query cannot ask for it by accident.
 * The body is fetched on its own for the one message being sent.
 *
 * `lastErrorCode` is ours — stable, enumerated by the error-code union, and what the
 * interface translates. `lastErrorDetail` is the RFC 7807 `detail`: another party's
 * prose, stored truncated, shown only to somebody debugging, never translated or parsed.
 */
/**
 * What a resource group name is called once it is ours. Groupie is another system's data,
 * so a stray space there is not a bug here — it is canonicalised on the way in, because
 * `openadr_message_group_unique` compares the stored bytes: `group-a ` alongside
 * `group-a` would be two `FLEX` events for one physical resource group.
 */
export const resourceGroupNameSchema = z.string().trim().min(1).max(200);

/**
 * The same name as it must already be in the column. Asserted rather than trimmed: a
 * stored value that still needs trimming means something wrote around the repository, and
 * folding it onto the canonical name here would hide the two rows doing it.
 *
 * `\S` at both ends is exactly "equal to its own `trim()`" — Postgres `btrim` with no
 * character set removes spaces only, so the column's CHECK spells the class out.
 */
const storedResourceGroupNameSchema = z
  .string()
  .max(200)
  .regex(/^\S(?:[\s\S]*\S)?$/);

export const openadrMessageSchema = z.object({
  id: openadrMessageIdSchema,
  requestId: requestIdSchema,
  resourceGroupName: storedResourceGroupNameSchema,
  status: messageStatusSchema,
  attempts: z.number().int().nonnegative(),
  /** Reads of `GET /reports` for this event. `attempts` counts `POST`s; these are not one. */
  reportAttempts: z.number().int().nonnegative(),
  claimedAt: z.coerce.date().nullable(),
  sentAt: z.coerce.date().nullable(),
  acknowledgedAt: z.coerce.date().nullable(),
  callieEventId: z.string().max(64).nullable(),
  failureReason: failureReasonSchema.nullable(),
  lastErrorCode: z.string().max(64).nullable(),
  lastErrorDetail: z.string().max(1000).nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type OpenadrMessage = z.infer<typeof openadrMessageSchema>;
